import {
  Component,
  ComponentInterface,
  Element,
  h,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import toggleCheckboxEvents from '../paf-toggle-checkbox/paf-toggle-checkbox.events';

/**
 * @slot unnamed - Slot to render paf-radio-checkbox items
 */

@Component({
  shadow: true,
  styleUrl: 'paf-radio-checkbox-group.scss',
  tag: 'paf-radio-checkbox-group',
})

export class PafRadioCheckboxGroup implements ComponentInterface {

  /**
   * The text for legend of the group
   */
  @Prop() public legend?: string;

  /**
   * Show toggle-checkbox. If true, a toggle is shown which enables/disables
   * all inputs in the group
   */
  @Prop() public showToggleCheckbox?: boolean;

  /**
 * The value for the checkbox
 */
  @Prop() public toggleCheckboxValue?: string;

  /**
  * The name for the checkbox
  */
  @Prop() public toggleCheckboxName?: string;

  /**
  * The text to show as label.
  */
  @Prop() public toggleCheckboxLabel?: string;

  /**
  * Define if it should be checked
  */
  @Prop() public toggleCheckboxChecked?: boolean;

  /**
   * Defines if a button should be implemented which checks/unchecks all items.
   * Only relevant for checkboxes (not for radios).
   */
  @Prop() public showCheckUncheckAllButton?: boolean;

  /**
   * Defines the text to use for the check all button.
   * Only if you set showCheckUncheckAllButton to true.
   */
  @Prop() public checkAllButtonText?: string;

  /**
   * Defines the text to use for the uncheck all button.
   * Only if you set showCheckUncheckAllButton to true.
   */
  @Prop() public uncheckAllButtonText?: string;

  @Element() private _element!: HTMLElement;

  @State() private _buttonText: string;

  @State() private _ariaLive: boolean;

  private _checkboxElements: NodeListOf<HTMLPafRadioCheckboxElement>;
  private _radioCheckboxElements: NodeListOf<HTMLPafRadioCheckboxElement>;
  @State() private _toggleCheckboxChecked: boolean;

  @Watch('toggleCheckboxChecked')
  public toggleCheckboxCheckedWatcher(newValue: boolean): void {
    this._toggleCheckboxChecked = newValue;
    this._disableOrEnableInputs();
  }

  public componentWillLoad(): Promise<void> | void {
    this._toggleCheckboxChecked = this.toggleCheckboxChecked;
  }

  public componentDidLoad(): void {
    // not optimal... onSlotChange event on the slot element does not fire
    // resiliently.
    // So we have no other option than doing this work in the componentDidLoad,
    // accepting that we might into undesired rerender on load occasionally
    this._handleSlotChange();
  }

  public disconnectedCallback(): void {
    this._checkboxElements?.forEach((elem) => {
      elem.removeEventListener('change', this._setButtonText);
    });

    this._element.removeEventListener(toggleCheckboxEvents.changed, this._handleToggleChange);
  }

  private _handleToggleChange = (evt): void => {
    this._toggleCheckboxChecked = evt.detail;
    this._disableOrEnableInputs();
  };

  private _disableOrEnableInputs = (): void => {
    this._radioCheckboxElements?.forEach((elem) => {
      elem.setAttribute('disabled', `${!this._toggleCheckboxChecked}`);
    });
  };

  private _setButtonText = (): void => {
    this._buttonText = this._allCheckboxesChecked()
      ? this.uncheckAllButtonText
      : this.checkAllButtonText;
  };

  private _toggleAriaLive = (): void => {
    this._ariaLive = !this._ariaLive;
  };

  private _allCheckboxesChecked = (): boolean => {
    let allChecked = true;

    this._checkboxElements?.forEach((elem) => {
      if (!elem.querySelector('input').checked) {
        allChecked = false;
      }
    });

    return allChecked;
  };

  private _handleCheckUncheckAll = (): void => {
    const allChecked = this._allCheckboxesChecked();

    this._checkboxElements?.forEach((elem) => {
      elem.querySelector('input').checked = !allChecked;

      if (allChecked) {
        elem.removeAttribute('checked');
      } else {
        elem.setAttribute('checked', 'true');
      }
    });

    this._setButtonText();
  };

  private _handleSlotChange = (): void => {
    if (this.showCheckUncheckAllButton) {
      this._checkboxElements = this._element.querySelectorAll('paf-radio-checkbox');

      this._checkboxElements?.forEach((elem) => {
        elem.addEventListener('change', this._setButtonText);
      });

      this._setButtonText();
    }

    if (this.showToggleCheckbox) {
      this._element.addEventListener(toggleCheckboxEvents.changed, this._handleToggleChange);
      this._radioCheckboxElements = this._element.querySelectorAll('paf-radio-checkbox');
      this._disableOrEnableInputs();
    }
  };

  public render(): HTMLDivElement {
    return (
      <fieldset class='fieldset'>
        {this.legend &&
          <legend
            class='legend-a11y'
            role='text'
          >
            {this.legend}
          </legend>
        }

        <div class='legend-wrapper'>
          {this.legend &&
            <div
              aria-hidden='true'
              class='legend'
              role='presentation'
            >
              {this.legend}
            </div>
          }

          {this.showToggleCheckbox &&
            <paf-toggle-checkbox
              value={this.toggleCheckboxValue}
              name={this.toggleCheckboxName}
              label={this.toggleCheckboxLabel}
              checked={this.toggleCheckboxChecked}
              data-testid='toggle'
            />
          }
        </div>

        <slot />

        {this.showCheckUncheckAllButton &&
          <button
            type='button'
            class='button'
            onClick={this._handleCheckUncheckAll}
            onFocusout={this._toggleAriaLive}
            onFocusin={this._toggleAriaLive}
            data-testid='button'
            aria-label={this._buttonText}
            aria-live={
              this._ariaLive
                ? 'assertive'
                : undefined
            }
            aria-atomic='false'
          >
            {this._buttonText}
          </button>
        }

      </fieldset>
    );
  }
}
